import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid2";

import { 
  Campaign,
  Letter,
} from "@dndModels";

import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import DandDAPI from "@utilities/smart_api";
import LoadingBox from "@components/input/LoadingBox";
import CommonUtilities from "@utilities/common_utilities";
import FlexBox from "@components/layout/FlexBox";
import RefreshButton from "@components/input/buttons/icon_buttons/RefreshButton";
import ViewArchivesCheckBox from "@dndComponents/campaign/ViewArchivesCheckBox";
import LetterInput from "./LetterInput";

interface Props {
  campaign: Campaign;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CampaignLettersInput({
  campaign,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const letter_param = searchParams.get("letter");

  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [view_archives, setViewArchives] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback( async () => {
    if (campaign.letters.length === 0) {
      campaign.letters = await api.getCampaignObjects<Letter>({ data_type: "campaign_letter", campaign });
    }
    setLoading(false);
  }, [campaign]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  // If the URL points at a letter that no longer exists once loaded, drop the param
  // rather than leaving a dead reference in the URL.
  useEffect(() => {
    if (fetched && !loading && letter_param) {
      const exists = campaign.letters.some(o => o._id === letter_param) || campaign.archived_letters.some(o => o._id === letter_param);
      if (!exists) {
        setSearchParams(prev => {
          const params = new URLSearchParams(prev);
          params.delete("letter");
          return params;
        }, { replace: true });
      }
    }
  }, [fetched, loading, letter_param, campaign.letters, campaign.archived_letters, setSearchParams]);

  const handleLetterOpenChange = useCallback((letter_id: string, open: boolean) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (open) {
        params.set("letter", letter_id);
      } else if (params.get("letter") === letter_id) {
        // Only clear if this letter owns the param — avoids a stale
        // close callback from a previously-open modal wiping a newer one.
        params.delete("letter");
      }
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const renderLetters = () => {
    return (
      <FlexBox>
        <Grid container spacing={0} direction="row">
          { (view_archives ? campaign.archived_letters : campaign.letters).map(letter => {
            return (
              <Grid size={{ xs:12, sm: 6, md: 4, lg: 3 }} key={letter._id}>
                <LetterInput
                  campaign={campaign}
                  letter={letter} 
                  autoOpen={letter._id === letter_param}
                  onOpenChange={(open: boolean) => handleLetterOpenChange(letter._id, open)}
                  onDelete={ async () => {
                    setProcessing(true);
                    await api.deleteObject("campaign_letter", letter);
                    campaign.letters = campaign.letters.filter(o => o._id !== letter._id);
                    campaign.archived_letters = campaign.archived_letters.filter(o => o._id !== letter._id);
                    setProcessing(false);
                  }} 
                  onArchive={ async () => {
                    setProcessing(true);
                    await api.setCampaignObjectArchived({ campaign, data_type: "campaign_letter", obj: letter, is_archived: !letter.is_archived });
                    setProcessing(false);
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
      </FlexBox>
    )
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  }
  return (
    <FlexBox direction="column" style={{ width: "100%" }}>
      { api.user && api.user._id === campaign.owner_id &&
        <FlexBox direction="row">
          <FlexBox xs_expand={0} className="padded">
            <AddButton
              disabled={processing}
              onClick={ async () => {
                const new_letter = new Letter();
                new_letter.campaign_id = campaign._id;
                setProcessing(true);
                await api.upsertObject("campaign_letter", new_letter);
                campaign.letters.push(new_letter);
                setProcessing(false);
              }}
            />
          </FlexBox>
          <FlexBox xs_expand={0} className="padded">
            <RefreshButton
              disabled={processing}
              onClick={ async () => {
                setProcessing(true);
                if (view_archives) {
                  await api.loadArchivedCampaignObjects({ campaign, data_type: "campaign_letter", refresh: true });
                } else {
                  campaign.letters = [];
                  campaign.letters = await api.getCampaignObjects<Letter>({ data_type: "campaign_letter", campaign });
                }
                setProcessing(false);
              }}
            />
          </FlexBox>
          <FlexBox xs_expand={0} className="padded">
            <ViewArchivesCheckBox
              campaign={campaign}
              data_type="campaign_letter"
              value={view_archives}
              disabled={processing}
              onChange={setViewArchives}
            />
          </FlexBox>
        </FlexBox>
      }
      { renderLetters() }
    </FlexBox>
  );
}
